import { useEffect, useState } from "react";
import { FiSend } from "react-icons/fi";
import { Modal, Button, Banner, ProgressBar } from "../common";
import { sortAlpha } from "../../utils/alphaSort";
import { inviteEmployeesBulk } from "../../services/invitationApi";

// [NEW] Sends login invitations to every selected employee in one go.
// Steps: confirm -> sending (progress) -> summary (sent / skipped / failed, with reasons).
// [FIXED] The list of people is copied when the window opens. Before, the page cleared its ticked rows as soon as the
// invitations were sent, the list became empty and the window closed before the "sent / skipped / failed" summary
// could be read, so it looked as if nothing had been sent.
export default function BulkInviteModal({ employees: incoming, onClose, onFinished }) {
  const [phase, setPhase] = useState("confirm"); // confirm | sending | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [outcome, setOutcome] = useState(null);
  const [employees, setEmployees] = useState([]);

  const requested = Array.isArray(incoming) && incoming.length > 0;
  useEffect(() => {
    if (requested && !employees.length) {
      setEmployees(incoming);
      setPhase("confirm"); setOutcome(null); setProgress({ done: 0, total: incoming.length });
    }
  }, [requested]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = employees.length > 0;
  const close = () => { setEmployees([]); onClose?.(); };

  const send = async () => {
    setPhase("sending");
    const res = await inviteEmployeesBulk(employees, { onProgress: setProgress });
    setOutcome(res);
    setPhase("done");
    onFinished?.(res);
  };

  const count = (kind) => outcome?.results.filter((r) => r.outcome === kind).length || 0;
  const problems = outcome?.results.filter((r) => r.outcome !== "sent") || [];
  const busy = phase === "sending";

  return (
    <Modal
      open={open}
      size="md"
      title={phase === "done" ? "Invitations processed" : `Send invitations to ${employees?.length || 0} ${employees?.length === 1 ? "employee" : "employees"}`}
      subtitle={phase === "confirm" ? "Creates a login for each person and emails them a link to choose a password. Each link works once and expires after 72 hours." : undefined}
      onClose={busy ? undefined : close}
      footer={
        phase === "confirm" ? <><Button variant="ghost" onClick={close}>Cancel</Button><Button icon={FiSend} onClick={send}>Send {employees?.length} invitation{employees?.length === 1 ? "" : "s"}</Button></>
          : phase === "sending" ? <Button loading disabled>Sending…</Button>
            : <Button onClick={close}>Close</Button>
      }
    >
      {phase === "confirm" && (
        <div className="form-stack">
          <Banner tone="info" title="What happens">
            Usernames are taken from each work email (the part before the @). Emails contain the username and the setup link only, never a password.
            Logins start with the Employee role. Anyone who already has a login is skipped.
          </Banner>
          <ul className="bulk-picker">
            {sortAlpha(employees).map((e) => <li key={e.id}><strong>{e.name}</strong> <span className="muted">{e.email || "no email on record"}</span></li>)}
          </ul>
        </div>
      )}

      {phase === "sending" && (
        <div className="form-stack">
          <p>Sending invitations… {progress.done} of {progress.total}. Please keep this window open.</p>
          <ProgressBar value={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} />
        </div>
      )}

      {phase === "done" && outcome && (
        <div className="form-stack">
          <Banner
            tone={count("failed") ? "warning" : "success"}
            title={`${count("sent")} sent${count("skipped") ? `, ${count("skipped")} skipped` : ""}${count("failed") ? `, ${count("failed")} failed` : ""}`}
          >
            {count("sent") > 0 && "The emails go out within a minute. Track delivery under Login invitations."}
          </Banner>
          {outcome.aborted && <Banner tone="danger" title="Stopped early">{outcome.aborted}</Banner>}
          {problems.length > 0 && (
            <ul className="bulk-picker">
              {sortAlpha(problems).map((r) => (
                <li key={r.id}>
                  <strong>{r.name}</strong> <span className={r.outcome === "failed" ? "form-error" : "muted"}>{r.outcome === "failed" ? "Failed: " : "Skipped: "}{r.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
